import { useState } from "react";
import { Check, Square, X } from "lucide-react";
import { RichTextBlocks } from "@/components/RichText";

const SHADOWS = [
  ["none", "Aucune"],
  ["sm", "Légère"],
  ["md", "Moyenne"],
  ["lg", "Prononcée"],
  ["xl", "Intense"],
];
const ALIGNS = [
  ["left", "⬅️ Gauche"],
  ["center", "↔️ Centré"],
  ["right", "➡️ Droite"],
  ["justify", "☰ Justifié"],
];
const ICONS = [
  ["", "Aucune icône"],
  ["lock", "🔒 Cadenas"],
  ["info", "ℹ️ Information"],
  ["star", "⭐ Étoile"],
  ["warning", "⚠️ Avertissement"],
  ["check", "✅ Validation"],
  ["shield", "🛡️ Bouclier"],
  ["gift", "🎁 Cadeau"],
  ["zap", "⚡ Éclair"],
  ["heart", "❤️ Cœur"],
  ["bell", "🔔 Cloche"],
  ["sparkles", "✨ Étincelles"],
];

const PRESETS = [
  ["warn", "⚠️ Avertissement (rouge)", { border: "#f87171", bg: "#fee2e2", op: 60, bw: 1, radius: 16, shadow: "sm", d3: false, glow: false, pad: 18, margin: 12, align: "left", icon: "warning" }],
  ["info", "ℹ️ Information (bleu)", { border: "#93c5fd", bg: "#eff6ff", op: 70, bw: 1, radius: 16, shadow: "sm", d3: false, glow: false, pad: 18, margin: 12, align: "left", icon: "info" }],
  ["tip", "✅ Conseil (vert)", { border: "#86efac", bg: "#ecfdf5", op: 70, bw: 1, radius: 16, shadow: "sm", d3: false, glow: false, pad: 18, margin: 12, align: "left", icon: "check" }],
  ["premium", "✨ Premium (fond transparent)", { border: "#c4b5fd", bg: "#ffffff", op: 0, bw: 1, radius: 20, shadow: "lg", d3: true, glow: true, pad: 22, margin: 14, align: "center", icon: "sparkles" }],
];

const EMPTY = {
  border: "#93c5fd", bg: "#eff6ff", op: 70, bw: 1, radius: 16,
  shadow: "md", d3: true, glow: false, pad: 18, margin: 12, align: "left", icon: "",
};

const buildToken = (cfg, content) => {
  const opts = [
    `border=${cfg.border}`, `bg=${cfg.bg}`, `op=${cfg.op}`, `bw=${cfg.bw}`,
    `radius=${cfg.radius}`, `shadow=${cfg.shadow}`, `d3=${cfg.d3 ? 1 : 0}`,
    `glow=${cfg.glow ? 1 : 0}`, `pad=${cfg.pad}`, `margin=${cfg.margin}`, `align=${cfg.align}`,
  ];
  if (cfg.icon) opts.push(`icon=${cfg.icon}`);
  return `:::box|${opts.join("|")}\n${content}\n:::`;
};

export default function BoxInserter({ onInsert, getSelected, testId = "box" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [text, setText] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const num = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }));

  const openPanel = () => {
    const sel = (getSelected && getSelected()) || "";
    setText(sel);
    setOpen(true);
  };

  const content = (text && text.trim()) ? text : "🔒 Titre du cadre\nVotre texte ici. Sélectionnez du texte avant de cliquer sur « Ajouter un cadre » pour l'encadrer automatiquement.";
  const previewToken = buildToken(form, content);

  const insert = () => {
    onInsert(buildToken(form, (text && text.trim()) ? text : "Votre texte ici"));
    setForm(EMPTY);
    setText("");
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`insert-box-btn-${testId}`}
        onClick={openPanel}
        className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:underline"
      >
        <Square className="h-3.5 w-3.5" /> Ajouter un cadre
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-3 rounded-xl border border-indigo-300 bg-indigo-50/50 p-3" data-testid={`box-panel-${testId}`}>
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map(([id, label, cfg]) => (
          <button
            key={id}
            type="button"
            data-testid={`box-preset-${testId}-${id}`}
            onClick={() => setForm(cfg)}
            className="rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-[11px] font-bold text-indigo-700 hover:bg-indigo-100"
          >
            {label}
          </button>
        ))}
      </div>

      <textarea
        data-testid={`box-text-${testId}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Contenu du cadre (mise en forme, alignement et liens acceptés)"
        className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
      />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Bordure
          <input type="color" data-testid={`box-border-${testId}`} value={form.border} onChange={upd("border")} className="h-8 w-9 cursor-pointer rounded border border-line" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Épaisseur
          <input type="number" min={0} max={12} data-testid={`box-bw-${testId}`} value={form.bw} onChange={num("bw")} className="h-8 w-14 rounded-lg border border-line px-2 text-xs" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Fond
          <input type="color" data-testid={`box-bg-${testId}`} value={form.bg} onChange={upd("bg")} className="h-8 w-9 cursor-pointer rounded border border-line" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Opacité fond
          <input type="range" min={0} max={100} data-testid={`box-op-${testId}`} value={form.op} onChange={num("op")} className="w-24 cursor-pointer accent-indigo-600" />
          <span className="w-8 text-right tabular-nums">{form.op}%</span>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Coins
          <input type="number" min={0} max={60} data-testid={`box-radius-${testId}`} value={form.radius} onChange={num("radius")} className="h-8 w-14 rounded-lg border border-line px-2 text-xs" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Espace int.
          <input type="number" min={0} max={60} data-testid={`box-pad-${testId}`} value={form.pad} onChange={num("pad")} className="h-8 w-14 rounded-lg border border-line px-2 text-xs" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Espace ext.
          <input type="number" min={0} max={60} data-testid={`box-margin-${testId}`} value={form.margin} onChange={num("margin")} className="h-8 w-14 rounded-lg border border-line px-2 text-xs" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Ombre
          <select data-testid={`box-shadow-${testId}`} value={form.shadow} onChange={upd("shadow")} className="h-8 rounded-lg border border-line px-2 text-xs">
            {SHADOWS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Alignement
          <select data-testid={`box-align-${testId}`} value={form.align} onChange={upd("align")} className="h-8 rounded-lg border border-line px-2 text-xs">
            {ALIGNS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Icône
          <select data-testid={`box-icon-${testId}`} value={form.icon} onChange={upd("icon")} className="h-8 rounded-lg border border-line px-2 text-xs">
            {ICONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-bold text-slate-600">
          <input type="checkbox" data-testid={`box-3d-${testId}`} checked={form.d3} onChange={(e) => setForm((f) => ({ ...f, d3: e.target.checked }))} className="h-4 w-4 accent-indigo-600" />
          Effet 3D / relief
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-bold text-slate-600">
          <input type="checkbox" data-testid={`box-glow-${testId}`} checked={form.glow} onChange={(e) => setForm((f) => ({ ...f, glow: e.target.checked }))} className="h-4 w-4 accent-indigo-600" />
          Effet lumineux
        </label>
      </div>

      <div className="rounded-lg bg-white p-3">
        <span className="mb-1 block text-[11px] text-slate-400">Aperçu :</span>
        <RichTextBlocks text={previewToken} className="text-sm text-slate-700" />
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          data-testid={`box-insert-${testId}`}
          onClick={insert}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white"
        >
          <Check className="h-3.5 w-3.5" /> Insérer le cadre
        </button>
        <button
          type="button"
          data-testid={`box-cancel-${testId}`}
          onClick={() => setOpen(false)}
          className="flex items-center gap-1.5 rounded-lg border border-line px-4 py-2 text-xs font-bold text-slate-600"
        >
          <X className="h-3.5 w-3.5" /> Annuler
        </button>
      </div>
    </div>
  );
}
